<template>
  <v-app id="advisor-app">
    <pc-singleton-modals id="singleton-advisor-modals"></pc-singleton-modals>
    <pc-singleton-overlay id="singleton-advisor-overlay"></pc-singleton-overlay>
    <pc-singleton-snackbar
      id="singleton-advisor-snackbar"
    ></pc-singleton-snackbar>

    <div v-if="initialAuthenticationRequestComplete" class="fill-height">
      <!-- Refused routes are shown here rather than redirected to: the address stays on screen,
           and the view that was refused never mounts, so it fires no requests. -->
      <pc-advisor-no-access
        v-if="blockedReason"
        :reason="blockedReason"
      ></pc-advisor-no-access>
      <router-view v-else></router-view>
    </div>
  </v-app>
</template>

<script>
import notifyPendo from "@/shared/lib/notifyPendo";
import PcAdvisorNoAccess from "@/advisor/views/PcAdvisorNoAccess";
import { goToClientPortal } from "@/shared/lib/client-portal-handoff";

export default {
  components: { PcAdvisorNoAccess },

  data: () => ({
    initialAuthenticationRequestComplete: false,
  }),

  computed: {
    // Only while the refusal still describes where the user is. Anything left over from a route
    // they have since navigated away from stops matching, and needs no clearing.
    blockedReason() {
      const blockedRoute = this.$store.state.blockedRoute;

      return blockedRoute && blockedRoute.path === this.$route.fullPath
        ? blockedRoute.reason
        : null;
    },
  },

  watch: {
    $route() {
      // Called whenever the route changes.
    },
  },

  methods: {
    // Called whenever the route changes
    routeChanged() {},
  },

  mounted: function () {
    if (
      window.amplifyRuntimeConfig.get("VUE_APP_ENVIRONMENT") === "production"
    ) {
      var script = document.createElement("script");
      script.setAttribute(
        "src",
        "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
      );
      script.setAttribute("type", "text/javascript");
      script.setAttribute("charset", "UTF-8");
      script.setAttribute(
        "data-domain-script",
        "0193dbc1-8261-7117-9f27-546361223712"
      );
      document.head.appendChild(script);
    }

    // This Advisor App component is only mounted once per page load, so this is the area to ADVISOR site bootstrapping

    // Initalize with the progress overlay
    var options = {
      subtext: "Loading...",
      opacity: 0.001,
    };
    this.$overlay.show(options);

    const isAnonymousRoute = () =>
      (this.$route.matched || []).some(
        (route) => route.meta && route.meta.allowsAnonymousUser
      );

    const completeAppInitialization = (user) => {
      this.$overlay.hide();
      this.initialAuthenticationRequestComplete = true;
      notifyPendo(user || null);
    };

    // Refresh authenticated user.
    if (!isAnonymousRoute() && this.$route.path !== "/") {
      this.$store.dispatch(
        "refreshAuthenticatedUser",
        function (user) {
          if (user && user.isStaffMember()) {
            document.location = window.amplifyRuntimeConfig.get(
              "VUE_APP_ADMIN_BASE_URL"
            );
          } else if (user && user.isClient()) {
            goToClientPortal(window.amplifyRuntimeConfig);
          } else {
            // all routing for advisor user is handled by router
            completeAppInitialization(user);
          }
        }.bind(this)
      );
    } else {
      return this.$nextTick(() => completeAppInitialization(null));
    }
  },
};
</script>

<style lang="scss">
#advisor-app {
  font-family: "Roboto", sans-serif;
  letter-spacing: 0px;

  .v-card__text {
    line-height: 18px !important;
    font-size: 14px !important;
  }
  @media (max-width: 960px) {
    body {
      width: max-content !important;
    }
    .pc-advisor-app-container {
      width: max-content !important;
    }
  }

  @media print {
    .pc-advisor-app-container {
      width: 100% !important;
    }

    .v-navigation-drawer {
      display: none;
    }

    .v-main {
      padding-left: 0 !important;
    }
  }

  // Vuetify v2.4 changed styling for nested grid items, causing spacing across the
  // app to be affected. A solution was found here that restores our previous spacing:
  // https://github.com/vuetifyjs/vuetify/issues/12848
  .row {
    margin-top: 0;
    margin-bottom: 0;
  }

  .row + .row {
    margin-top: 0;
  }
}

// The toast of an arrival, sliding up from below the fold as it fades in. Said from the store,
// which reaches the snackbar singleton directly; only its entrance is dressed here.
//
// Not scoped, and not in the snackbar component either: Vue puts these classes on Vuetify's
// `.v-snack__wrapper`, which no component's scoped styles reach. Both classes are named in each
// selector because Vuetify declares .15s on that element directly, and only that outweighs it.
.acting-as-toast-enter.v-snack__wrapper,
.acting-as-toast-leave-to.v-snack__wrapper {
  transform: translateY(80px);
  opacity: 0;
}

.acting-as-toast-enter-active.v-snack__wrapper,
.acting-as-toast-leave-active.v-snack__wrapper {
  // The two are not the same length in the design: it is fully opaque halfway through the rise,
  // so it finishes travelling rather than finishing appearing.
  transition-property: opacity, transform;
  transition-duration: 300ms, 600ms;
  // ease-out, on both, as the design's timeline has it.
  transition-timing-function: cubic-bezier(0, 0, 0.58, 1);
}

html {
  overflow: auto;
}

.requiredField:before {
  content: " *";
  color: #d32f2f;
  margin-right: 5px;
  margin-top: 5px;
  font-size: 15px;
}

.requiredFieldText .requiredField:before {
  vertical-align: top;
}

.requiredFieldText {
  vertical-align: text-top;
  font-size: 13px;
  color: #989898;
}
</style>
